import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '../../primitives/index.js'
import { useCallback, useEffect, useState } from 'react'
import type { DefaultValues, FieldErrors, FieldValues } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { FormComponentContext } from './form/component-provider.js'
import BooleanSchemaField from './form/controls/boolean.js'
import DateTimeSchemaField from './form/controls/datetime.js'
import EnumSchemaField from './form/controls/enum.js'
import ListSchemaField from './form/controls/list.js'
import LiteralSchemaField from './form/controls/literal.js'
import NumberSchemaField from './form/controls/number.js'
import ObjectSchemaField from './form/controls/object.js'
import TextSchemaField from './form/controls/text.js'
import { useSchemaForm } from './schema.hook.js'
import type { ZodAny } from 'zod'

import type { HTMLAttributes } from 'react'

export interface AutoFormProps<T extends FieldValues>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit' | 'onError'> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any // We use any to avoid deep type inference
  defaultValues?: DefaultValues<T>
  /**
   * If submitButton is specified, the form will submit on click. Otherwise, it will be submitted on every change
   * when the form is valid.
   */
  submitButton?: string
  /** Receive a callback when the submit button is clicked with a valid schema */
  onSubmit: (value: T) => void

  onError?: (errors: FieldErrors<T>) => void
}

const formComponents = {
  text: TextSchemaField,
  // eslint-disable-next-line id-blacklist
  number: NumberSchemaField,
  enum: EnumSchemaField,
  datetime: DateTimeSchemaField,
  list: ListSchemaField,
  literal: LiteralSchemaField,
  // eslint-disable-next-line id-blacklist
  boolean: BooleanSchemaField,
  object: ObjectSchemaField,
}

/** For zod validation, @see https://github.com/react-hook-form/resolvers#zod */
export function AutoForm<T extends FieldValues = FieldValues>(props: AutoFormProps<T>) {
  const { schema, defaultValues = {}, submitButton, onSubmit, onError, style, ...divProps } = props

  const mode = submitButton ? 'onSubmit' : 'onChange'
  const form = useSchemaForm(schema)

  // Using explicit resolver without lazy type to reduce type inference complexity
  const {
    register,
    watch,
    handleSubmit,
    trigger,
    control,
    formState: { errors },
  } = useForm({
    mode,
    shouldFocusError: mode === 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: defaultValues as DefaultValues<T>,
  })

  const [complete, setComplete] = useState(false)

  const hasError = Object.keys(errors).length > 0

  const handleChange = useCallback(
    async (values: T) => {
      /** Errors is not updated here... */
      if (!(await trigger())) {
        return
      }

      onSubmit(values as T)
    },
    [onSubmit, trigger]
  )

  const handleError = useCallback(
    (errors: FieldErrors<T>) => {
      if (onError) {
        onError(errors)
      }
    },
    [onError]
  )

  useEffect(() => {
    if (!hasError) {
      return
    }
    handleError(errors)
  }, [errors, handleError, hasError])

  /** Submit when mode is onChange, formValues has changed and validate */
  useEffect(() => {
    const { unsubscribe } = watch(async data => {
      // Verify schema
      const zodSchema: ZodAny = schema
      const parseResult = zodSchema.safeParse(data)
      setComplete(parseResult.success)

      if (mode !== 'onChange') {
        return
      }
      await handleChange(data as T)
    })

    return () => {
      unsubscribe()
    }
  }, [schema, mode, handleChange, watch, trigger])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...style }} {...divProps}>
      <form onSubmit={handleSubmit(handleChange, handleError)}>
        <FormComponentContext value={formComponents as never}>
          <ObjectSchemaField name="" spec={form} register={register} control={control} errors={errors} />

          {submitButton ? (
            <Button type="submit" disabled={hasError || !complete}>
              {submitButton}
            </Button>
          ) : null}
        </FormComponentContext>
      </form>
    </div>
  )
}

export default AutoForm
